import { useCallback, useRef } from "react";
import { useMutation } from "@apollo/client/react";

import type { MapPin } from "#app/components/map/map-types";
import { ReverseGeocodeAddressDocument } from "#app/lib/data";

export function useCreateMapPin() {
  const [reverseGeocode] = useMutation(ReverseGeocodeAddressDocument);
  const requestIdRef = useRef(0);

  const createPin = useCallback(
    async (latitude: number, longitude: number): Promise<MapPin | null> => {
      const currentRequestId = ++requestIdRef.current;
      try {
        const { data } = await reverseGeocode({
          variables: { lat: latitude, lng: longitude },
        });
        if (currentRequestId !== requestIdRef.current) return null;

        const address = data?.reverseGeocodeAddress;
        if (!address) return null;
        const resolvedLatitude = address.latLng?.lat ?? latitude;
        const resolvedLongitude = address.latLng?.lng ?? longitude;
        return {
          id: `new-${currentRequestId}-${resolvedLatitude}-${resolvedLongitude}`,
          latitude: resolvedLatitude,
          longitude: resolvedLongitude,
          color: "grey",
          count: 0,
          issueIds: [],
          isLocal: true,
          addressId: address.addressId,
          // Absent stays absent: an empty string would shadow every fallback
          // the infobox title runs through.
          addressLine1: address.addressLine1 ?? undefined,
          wholeAddress: address.wholeAddress ?? undefined,
          city: address.city ?? undefined,
          stateProvince: address.stateProvince ?? undefined,
          postalCode: address.postalCode ?? undefined,
          propertyDetails: address.propertyDetails ?? null,
        };
      } catch (error: unknown) {
        if (currentRequestId === requestIdRef.current) {
          console.error("Failed to resolve map pin:", error);
        }
        return null;
      }
    },
    [reverseGeocode],
  );

  const cancel = useCallback(() => {
    requestIdRef.current++;
  }, []);

  return { createPin, cancel };
}
